<template>
  <div
    class="input autocomplete"
    :class="[
      attrClass,
      `selector-search-element-${name}`,
      {
        'input-error': errorMessage
      }
    ]"
    :style="attrStyle"
  >
    <Combobox
      :model-value="selected"
      as="div"
      :nullable="true"
      class="relative"
      :by="compare"
      @update:model-value="updateSelected"
    >
      <label v-if="label" class="label input-label autocomplete-label">
        {{ label }}
      </label>
      <div class="autocomplete-inputContainer relative cursor-default">
        <ComboboxInput
          :id="`selector-search-input-${name}`"
          ref="searchInput"
          :display-value="formatterDisplayValues"
          :placeholder="placeholder"
          class="input-field autocomplete-field w-full"
          autocomplete="off"
          v-bind="attrs"
          :model-value="searchValue || selected"
          @change="updateSearch($event.target.value)"
          @focus="onFocus"
          @click="onFocus"
          @blur="onBlur"
          @keydown.arrow-down="openOptions"
          @keydown.escape="closeOptions"
        />
        <div class="icons">
          <slot></slot>
          <button
            v-if="(selected || searchValue) && clearable"
            type="button"
            @click="clear"
          >
            <Icon :path="mdiClose" class="clearIcon text-neutral-light w-6" />
          </button>
          <button
            :id="`selector-search-button-${name}`"
            type="button"
            class="flex items-center"
            tabindex="-1"
            @mousedown="onButton"
            @blur="onBlur"
          >
            <slot name="button">
              <Icon
                :path="open ? mdiChevronUp : mdiChevronDown"
                :class="[
                  open
                    ? 'autocomplete-fieldIconOpen'
                    : 'autocomplete-fieldIconClosed',
                  'autocomplete-fieldIcon'
                ]"
              />
            </slot>
          </button>
        </div>
      </div>
      <Transition
        enter-active-class="transition duration-[0.21] ease-in-out"
        enter-from-class="transform scale-90 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-[0.1] ease-in-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-90 opacity-0"
        @after-leave="leave"
      >
        <ComboboxOptions
          v-if="filteredOptions && open"
          static
          class="absolute z-[4] mt-1 max-h-60 min-w-full max-w-full max-h-[152px] overflow-auto py-1 !text-sm dropdown-menu"
          :class="`selector-search-element-${name}`"
          tabindex="0"
        >
          <div
            v-if="!filteredOptions || !filteredOptions.length"
            class="relative cursor-default select-none py-2 px-4 text-neutral"
          >
            <span class="text-neutral-light">
              {{ $t('common.noResults') }}
            </span>
          </div>
          <div
            v-if="loading"
            class="relative cursor-default select-none py-2 px-4 text-neutral"
          >
            <Icon
              :path="mdiLoading"
              class="text-neutral-light h-5 w-5 mx-auto animate-spin"
            />
          </div>

          <ComboboxOption
            v-for="(option, index) in filteredOptions"
            :key="index"
            v-slot="{ selected: optionIsSelected, active }"
            as="template"
            :value="option"
            :disabled="option.disabled"
          >
            <li
              :class="[
                active
                  ? 'autocomplete-optionActive menu-itemActive'
                  : 'autocomplete-optionDefault',
                'autocomplete-option menu-item',
                'relative select-none py-2 !pl-10 pr-4'
              ]"
            >
              <span
                class="block truncate"
                :class="{
                  'opacity-50': option.disabled
                }"
              >
                <slot
                  name="option"
                  v-bind="typeof option === 'object' ? option : { option }"
                >
                  {{ option?.text || option }}
                </slot>
              </span>
              <span
                v-if="optionIsSelected"
                class="absolute inset-y-0 left-0 flex items-center pl-3"
                :class="[
                  active
                    ? 'autocomplete-optionIconActive'
                    : 'autocomplete-optionIconDefault',
                  'autocomplete-optionIcon'
                ]"
              >
                <Icon
                  v-if="optionIsSelected"
                  class="text-primary"
                  :path="mdiCheckBold"
                />
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </Transition>
    </Combobox>
    <span v-if="errorMessage" class="input-errorContainer">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script lang="ts">
import {
  Combobox,
  // ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/vue';
import {
  mdiCheckBold,
  mdiChevronDown,
  mdiChevronUp,
  mdiClose,
  mdiLoading
} from '@mdi/js';
import { useField } from 'vee-validate';
import type { PropType } from 'vue';

import type { Rule } from '@/composables/use-rules';
import { capitalizeWords } from '@/helpers/search';
import type { SelectOption } from '@/types/common';
import { compareArrays, getSelectorItemValue } from '@/utils';
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
const { class: attrClass, style: attrStyle, ...attrs } = useAttrs();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Value = any;
type Item = Record<string, Value>;

type Options = (string | SelectOption<any>)[] | null;

type OptionsProp =
  | ((search?: string) => Promise<Options>)
  | ((search?: string) => Options)
  | Options;

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [Object, String, Array] as PropType<Value>,
    default: () => null
  },
  search: {
    type: String,
    default: ''
  },
  options: {
    type: [Array, Function] as PropType<OptionsProp>,
    default: () => []
  },
  textCallback: {
    type: Function as PropType<(option: unknown) => string>
  },
  rules: {
    type: Array as PropType<Rule[]>,
    default: () => []
  },
  name: {
    type: String,
    default: () => 'mySelector'
  },
  clearable: {
    type: Boolean,
    default: () => false
  },
  text: {
    type: String,
    default: 'text'
  }
});

type Emits = {
  (e: 'update:modelValue', value: Value, oldValue: Value): void;
  (e: 'update:search', value: Value, oldValue: Value): void;
  (e: 'item-selected', item: Item, oldItem: Item): void;
  (e: 'validate', value: boolean | string): void;
};

const emit = defineEmits<Emits>();

const open = ref(false);

const selected = ref();

const searchInput = ref<InstanceType<typeof ComboboxInput> | null>(null);

const loading = ref(false);

const doneOptions = ref([]);

const debouncedUpdateOptions = debounce(updateOptions, 250);

async function updateOptions() {
  let newOptions: Options | Promise<Options>;

  if (typeof props.options === 'function') {
    newOptions = props.options(searchValue.value);
  } else {
    newOptions = props.options;
  }

  const onDone = (options: Options) => {
    const newOptions = options?.map(option => {
      if (typeof option === 'object') {
        return option;
      }
      return {
        value: option,
        text: option
      };
    });

    if (!compareArrays(doneOptions.value, newOptions)) {
      doneOptions.value = newOptions;
    }

    loading.value = false;
  };

  if (newOptions instanceof Promise) {
    loading.value = true;
    onDone(await newOptions);
  } else {
    onDone(newOptions);
  }
}

const filteredOptions = computed(() => {
  if (
    typeof props.options !== 'function' &&
    searchValue.value &&
    doneOptions.value
  ) {
    const found = doneOptions.value.filter(option => {
      return option[props.text]
        .toLowerCase()
        .includes(searchValue.value.toLowerCase());
    });
    if (found.length) {
      return found;
    }
  }
  return doneOptions.value;
});

const {
  errorMessage,
  value: validateValue,
  validate: validateField
} = useField(props.name, useRules(props.rules));

let hasValidated = false;

const validate = async (isExternalCall = false, force = false) => {
  if (!isExternalCall || hasValidated || force) {
    hasValidated = true;
    await validateField();
  }
  if (!isExternalCall) {
    emit('validate', errorMessage.value || false);
  }
  return errorMessage.value || false;
};

watch(
  selected,
  (item, oldItem) => {
    const value = getSelectorItemValue(item);
    const oldValue = getSelectorItemValue(oldItem);
    validateValue.value = value;
    if (value === props.modelValue) {
      return;
    }
    emit('update:modelValue', value, oldValue);
    emit('item-selected', item, oldItem);
  },
  { deep: true }
);

watch(
  () => props.modelValue,
  value => {
    if (selected.value && value && selected.value.value === value) {
      return;
    }
    const foundItem = doneOptions.value?.find(o => {
      if (typeof o !== 'object') {
        return o === value;
      }
      return o.value === value;
    });
    selected.value = foundItem || value;
  },
  { immediate: true, deep: true }
);

const searchValue = ref('');

watch(
  () => props.search,
  value => {
    if (typeof value === 'string' && searchValue.value !== value) {
      searchValue.value = value;
    }
  }
);

const debouncedEmitSearch = debounce((value, oldValue) => {
  if (typeof value === 'string' && props.search !== value) {
    emit('update:search', value, oldValue);
  }
  if (
    searchInput.value &&
    searchInput.value?.$el?.value !== value &&
    typeof value === 'string' &&
    (value || !selected.value?.value)
  ) {
    searchInput.value.$el.value = !selected.value?.value ? '' : value;
  }
  debouncedUpdateOptions();
}, 250);

watch(() => searchValue.value, debouncedEmitSearch);

const compare = (a: Value, b: Value) => {
  return getSelectorItemValue(a) === getSelectorItemValue(b);
};

const updateSelected = (value: Value) => {
  selected.value = value;
  searchValue.value = '';
  if (selected.value) {
    setTimeout(() => {
      open.value = false;
    }, 100);
  }
};

const formatterDisplayValues = (items: Value | Array<Value>) => {
  let text = '';
  if (Array.isArray(items)) {
    text = items
      .map(item => {
        return item?.[props.text] || item;
      })
      .join(', ');
  } else if (items) {
    text = items?.[props.text] || items;
  }
  return typeof text === 'string' ? capitalizeWords(text) : text;
};

const openOptions = () => {
  if (!open.value) {
    open.value = true;
  }
};

const closeOptions = () => {
  open.value = false;
};

const updateSearch = (value: string) => {
  if (typeof value === 'string' && searchValue.value !== value) {
    searchValue.value = value;
  }

  openOptions();
};

const clear = () => {
  selected.value = null;
  searchValue.value = '';
};

const leave = () => {
  searchValue.value = '';
};

function onFocus() {
  debouncedUpdateOptions();
  open.value = true;
}

function onBlur(event: FocusEvent) {
  // if event is outside of the component, close the dropdown
  if (
    !event.relatedTarget ||
    (event.relatedTarget as HTMLElement)?.closest?.(
      `.selector-search-element-${props.name}`
    ) ||
    (event.relatedTarget as HTMLElement).classList.contains(
      `selector-search-element-${props.name}`
    )
  ) {
    return;
  }
  searchValue.value = '';
  validate();
  open.value = false;
}

function onButton() {
  open.value = !open.value;
}

defineExpose({
  validate
});
</script>
